import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft,
  CheckCircle2,
  DatabaseBackup,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Trash2,
  PlusCircle,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const formatBytes = (value = 0) => {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const SGT_TIME_OPTS = {
  hour12: false,
  timeZone: 'Asia/Singapore',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.toLocaleString('en-SG', SGT_TIME_OPTS)} SGT`;
};

const formatRelative = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

const statusColor = (status) => {
  if (status === 'ok') return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (status === 'error') return 'text-rose-700 bg-rose-50 border-rose-200';
  return 'text-slate-700 bg-slate-50 border-slate-200';
};

export default function BackupManagement({ onBack }) {
  const { token } = useAuth();

  const [environment, setEnvironment] = useState('');
  const [appEnvironment, setAppEnvironment] = useState('dev');
  const [items, setItems] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedBackupId, setSelectedBackupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [restoreMode, setRestoreMode] = useState('full');
  const [collectionName, setCollectionName] = useState('');
  const [note, setNote] = useState('');
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveListError, setDriveListError] = useState('');

  const selectedBackup = useMemo(
    () => items.find((item) => item.id === selectedBackupId) || null,
    [items, selectedBackupId],
  );

  const selectedCount = selectedRows.length;
  const allSelected = items.length > 0 && selectedRows.length === items.length;

  const api = async (path, options = {}) => {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.detail || 'Request failed');
    }
    return payload;
  };

  const fetchEnvironment = async () => {
    const payload = await api('/api/backups/settings/environment');
    setEnvironment(payload.environment || 'dev');
    setAppEnvironment(payload.app_environment || payload.environment || 'dev');
  };

  const fetchBackups = async (targetEnv = environment) => {
    setLoading(true);
    setError('');
    try {
      const payload = await api(`/api/backups?environment=${targetEnv}`);
      const incoming = payload.items || [];
      if (payload.app_environment) {
        setAppEnvironment(payload.app_environment);
      }
      setDriveStatus(payload.drive_status || null);
      setDriveListError(payload.drive_list_error || '');
      setItems(incoming);
      setSelectedRows((prev) => prev.filter((id) => incoming.some((item) => item.id === id)));
      if (incoming.length && !incoming.some((item) => item.id === selectedBackupId)) {
        setSelectedBackupId(incoming[0].id);
      }
    } catch (fetchError) {
      setError(fetchError.message || 'Unable to fetch backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        await fetchEnvironment();
      } catch (e) {
        setError(e.message || 'Unable to fetch backup environment');
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!environment) return;
    fetchBackups(environment);
  }, [environment]);

  const updateEnvironment = async (nextEnv) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api('/api/backups/settings/environment', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment: nextEnv }),
      });
      setEnvironment(nextEnv);
      if (nextEnv !== appEnvironment) {
        setSuccess(`Switched view to ${nextEnv.toUpperCase()} backups. Creation remains locked to ${appEnvironment.toUpperCase()}.`);
      }
      setSelectedRows([]);
      setSelectedBackupId('');
      if (nextEnv === appEnvironment) {
        setSuccess(`Switched to ${nextEnv.toUpperCase()} backups.`);
      }
    } catch (envError) {
      setError(envError.message || 'Environment update failed');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = await api('/api/backups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note || null }),
      });
      setSuccess(`Backup ${payload.id} created.`);
      setNote('');
      await fetchBackups(environment);
      setSelectedBackupId(payload.id);
    } catch (createError) {
      setError(createError.message || 'Backup creation failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    setSelectedBackupId(id);
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRows([]);
      return;
    }
    setSelectedRows(items.map((item) => item.id));
  };

  const deleteSelected = async () => {
    if (!selectedRows.length) {
      setError('Select backups first.');
      return;
    }
    if (!window.confirm(`Delete ${selectedRows.length} selected backup(s)?`)) return;

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const bulkPayload = await api(`/api/backups/bulk-delete?environment=${environment}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backup_ids: selectedRows }),
      });
      setSuccess(`Deleted ${bulkPayload.deleted_count} backup(s).`);
      setSelectedRows([]);
      await fetchBackups(environment);
    } catch (deleteError) {
      setError(deleteError.message || 'Bulk delete failed');
    } finally {
      setLoading(false);
    }
  };

  const restoreBackup = async (backupId) => {
    const targetId = backupId || selectedBackupId;
    if (!targetId) {
      setError('Select a backup to restore.');
      return;
    }
    if (restoreMode === 'collection' && !collectionName.trim()) {
      setError('Collection name required for collection restore mode.');
      return;
    }
    if (!window.confirm(`Restore from backup ${targetId}?`)) return;

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = await api(`/api/backups/${targetId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restore_mode: restoreMode,
          collection_name: restoreMode === 'collection' ? collectionName.trim() : null,
          environment,
        }),
      });
      setSuccess(`Restore completed (${payload.restore_mode}) from ${targetId}.`);
    } catch (restoreError) {
      setError(restoreError.message || 'Restore failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="backup-management-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold text-slate-900" data-testid="backup-management-title">Backup Management</h1>
          <Button variant="outline" onClick={onBack} data-testid="backup-management-back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="backup-management-error-alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert data-testid="backup-management-success-alert">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Card data-testid="backup-status-card">
            <CardHeader className="pb-2">
              <CardDescription>Status</CardDescription>
              <CardTitle className="text-lg flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" /> Connected
              </CardTitle>
            </CardHeader>
          </Card>

          <Card data-testid="backup-environment-card">
            <CardHeader className="pb-2">
              <CardDescription>Environment</CardDescription>
              <CardTitle className="text-lg flex items-center gap-2">
                <Badge className="uppercase" data-testid="backup-environment-badge">{environment}</Badge>
              </CardTitle>
              <CardDescription className="text-xs pt-1" data-testid="backup-app-environment-note">
                Create target: {appEnvironment.toUpperCase()}
              </CardDescription>
            </CardHeader>
          </Card>

          <Card data-testid="backup-total-card">
            <CardHeader className="pb-2">
              <CardDescription>Total Backups</CardDescription>
              <CardTitle className="text-lg flex items-center gap-2">
                <DatabaseBackup className="w-5 h-5 text-indigo-700" /> {items.length}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card data-testid="backup-schedule-card">
            <CardHeader className="pb-2">
              <CardDescription>Auto Backup</CardDescription>
              <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                <ShieldCheck className="w-5 h-5" /> 00:00 SGT Daily
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        <Card data-testid="backup-files-card">
          <CardHeader>
            <CardTitle className="text-2xl">Backup Files</CardTitle>
            <CardDescription>View and manage backup files across environments. Local dev can list LIVE backups from Google Drive when configured, or from backup JSON already saved under backend/data/backups.</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {environment === 'live' && driveStatus && !driveStatus.drive_listing_available && (
              <Alert data-testid="backup-drive-config-hint">
                <AlertDescription>
                  Google Drive listing is not configured on this backend
                  {driveListError ? `: ${driveListError}` : ''}.
                  {' '}Add GOOGLE_SERVICE_ACCOUNT_JSON (or GOOGLE_SERVICE_ACCOUNT_FILE) and GOOGLE_DRIVE_LIVE_FOLDER_ID to backend/.env, then restart uvicorn.
                  {' '}Any Live backup JSON files already in backend/data/backups will still appear here.
                </AlertDescription>
              </Alert>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button
                  variant={environment === 'live' ? 'default' : 'outline'}
                  onClick={() => updateEnvironment('live')}
                  disabled={loading}
                  data-testid="backup-env-live-button"
                >
                  Live
                </Button>
                <Button
                  variant={environment === 'dev' ? 'default' : 'outline'}
                  onClick={() => updateEnvironment('dev')}
                  disabled={loading}
                  data-testid="backup-env-dev-button"
                >
                  Dev
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <Input
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="manual backup note"
                  className="w-60"
                  data-testid="backup-note-input"
                />
                <Button onClick={createBackup} disabled={loading} data-testid="create-backup-button">
                  <PlusCircle className="w-4 h-4 mr-2" /> Create
                </Button>
                <Button variant="outline" onClick={() => fetchBackups(environment)} disabled={loading} data-testid="refresh-backups-button">
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
                <Button variant="destructive" onClick={deleteSelected} disabled={loading || !selectedCount} data-testid="delete-selected-backups-button">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Selected ({selectedCount})
                </Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden" data-testid="backup-table-wrap">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="p-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        data-testid="backup-select-all-checkbox"
                      />
                    </th>
                    <th className="p-3 text-left">File Name</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Size</th>
                    <th className="p-3 text-left">Integrity</th>
                    <th className="p-3 text-left">Created (SGT)</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-6 text-center text-slate-500" data-testid="backup-empty-row">No backups in this environment.</td>
                    </tr>
                  )}

                  {items.map((item) => {
                    const isSelected = selectedRows.includes(item.id);
                    return (
                      <tr key={item.id} className={`${isSelected ? 'bg-sky-50' : 'bg-white'} border-t`} data-testid={`backup-table-row-${item.id}`}>
                        <td className="p-3 align-top">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(item.id)}
                            data-testid={`backup-select-checkbox-${item.id}`}
                          />
                        </td>
                        <td className="p-3 align-top">
                          <p className="font-medium text-slate-900" data-testid={`backup-filename-${item.id}`}>{item.file_name || `${item.id}.json`}</p>
                          {item.drive_web_view_link && (
                            <a
                              href={item.drive_web_view_link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-sky-700 underline"
                              data-testid={`backup-drive-link-${item.id}`}
                            >
                              Open in Drive
                            </a>
                          )}
                        </td>
                        <td className="p-3 align-top">
                          <Badge variant="outline" data-testid={`backup-type-badge-${item.id}`}>{item.trigger || 'manual'}</Badge>
                        </td>
                        <td className="p-3 align-top" data-testid={`backup-size-${item.id}`}>{formatBytes(item.size_bytes)}</td>
                        <td className="p-3 align-top">
                          <Badge className={`border ${statusColor(item.drive_sync)}`} data-testid={`backup-integrity-badge-${item.id}`}>
                            {item.drive_sync === 'ok' ? 'Complete' : item.drive_sync === 'error' ? 'Error' : 'Local'}
                          </Badge>
                        </td>
                        <td className="p-3 align-top" data-testid={`backup-created-${item.id}`}>
                          <div>{formatDateTime(item.created_at)}</div>
                          <div className="text-xs text-slate-500">{formatRelative(item.created_at)}</div>
                        </td>
                        <td className="p-3 align-top">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedBackupId(item.id);
                                restoreBackup(item.id);
                              }}
                              disabled={loading}
                              data-testid={`backup-restore-button-${item.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" /> Restore
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={async () => {
                                if (!window.confirm(`Delete backup ${item.id}?`)) return;
                                setLoading(true);
                                setError('');
                                setSuccess('');
                                try {
                                  const payload = await api(`/api/backups/${item.id}?environment=${environment}`, {
                                    method: 'DELETE',
                                  });
                                  setSuccess(`Backup ${payload.id} deleted.`);
                                  setSelectedRows((prev) => prev.filter((rowId) => rowId !== item.id));
                                  await fetchBackups(environment);
                                } catch (deleteError) {
                                  setError(deleteError.message || 'Delete failed');
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              disabled={loading}
                              data-testid={`backup-delete-button-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2" data-testid="backup-restore-options-panel">
              <div className="space-y-1.5">
                <Label>Restore Mode</Label>
                <Select value={restoreMode} onValueChange={setRestoreMode}>
                  <SelectTrigger data-testid="backup-restore-mode-select-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full" data-testid="backup-restore-mode-full-option">Full Restore (all collections)</SelectItem>
                    <SelectItem value="collection" data-testid="backup-restore-mode-collection-option">Single Collection Restore</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Collection (single restore)</Label>
                <Input
                  value={collectionName}
                  onChange={(event) => setCollectionName(event.target.value)}
                  disabled={restoreMode !== 'collection'}
                  placeholder="pattern_versions"
                  data-testid="backup-restore-collection-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
